<template>
  <div>
    <label v-if="label" class="form-label" :class="{ required }">{{ label }}</label>
    <div v-if="hint" class="hint-text">{{ hint }}</div>

    <ul v-if="locationRows.length" class="location-list">
      <li v-for="row in locationRows" :key="row.key" class="location-row">
        <div class="location-text">
          <div class="location-address">{{ row.location.formatted_address }}</div>
          <div v-if="row.parts" class="location-parts">{{ row.parts }}</div>
        </div>
        <LeadButton variant="icon" :aria-label="$t('btn.delete')" @click="remove(row.index)">
          <template #icon>
            <IconX size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </li>
    </ul>

    <LeadCombobox
      v-if="!isFull"
      :items="matches"
      getOptionLabel="formatted_address"
      getOptionValue="place_id"
      :modelValue="null"
      :placeholder="placeholder"
      :loading="isLoading"
      :disableFiltering="true"
      @search="onSearch"
      @update:modelValue="add" />

    <div v-else class="hint-text">{{ $t('campaigns.manual.location_limit_reached') }}</div>

    <FormErrorList :errors="errors" />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { debounce, FormErrorList } from '@prospective/lithium';
import { IconX } from '@tabler/icons-vue';
import { isAbortError } from '@/api/client';
import { geocodeAddress } from '@/api/geocoding';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadCombobox from '@/components/form/LeadCombobox.vue';
import translator from '@/locale/translator';
import type { GeocodedLocation } from '@/types/types';

/**
 * One or more places on a manual booking, resolved server-side through the geocoding endpoint.
 *
 * The object the API returns is stored VERBATIM. PJAS validates the same shape it produced, so
 * picking fields out or renaming them here is how a stored location stops matching the address it
 * was resolved from.
 *
 * The value is always a list, even where the type caps it at one place — a single-location field is
 * a cap of 1, not a different shape.
 */
const modelValue = defineModel<GeocodedLocation[] | null>({ default: null });

const props = withDefaults(
  defineProps<{
    label?: string;
    hint?: string;
    errors?: string[];
    required?: boolean;
    maxLocations?: number | null;
  }>(),
  { errors: () => [], required: false }
);

const matches = ref<GeocodedLocation[]>([]);
const isLoading = ref(false);

/** The in-flight geocode, so a slower earlier answer cannot overwrite a newer one. */
let searchController: AbortController | null = null;

/**
 * The value as a list. A stored document may be keyed rather than a list — a script prefill on the
 * PJAS side produces a 1-indexed one — so it is read through `Object.values`.
 */
const locations = computed<GeocodedLocation[]>(() => {
  const value = modelValue.value;
  if (!value) return [];

  const entries: GeocodedLocation[] = Array.isArray(value) ? value : Object.values(value);

  return entries.filter((entry) => !!entry);
});

/** The rows as rendered, so the parsed parts are built once per change rather than twice per row. */
const locationRows = computed(() =>
  locations.value.map((location, index) => ({
    location,
    index,
    key: location.place_id ?? index,
    parts: [[location.zip, location.city].filter(Boolean).join(' '), location.country].filter(Boolean).join(' · '),
  }))
);

const isFull = computed(() => typeof props.maxLocations === 'number' && locations.value.length >= props.maxLocations);

const placeholder = computed(() =>
  locations.value.length
    ? translator.get('campaigns.manual.add_address')
    : translator.get('campaigns.manual.search_address')
);

const searchDebounced = debounce(search, 400);

function onSearch(query: string) {
  if (!query || query.trim() === '') {
    searchController?.abort();
    matches.value = [];
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  searchDebounced(query);
}

async function search(address: string) {
  // a keystroke past an in-flight lookup makes that lookup's answer worthless
  searchController?.abort();

  const controller = new AbortController();
  searchController = controller;

  try {
    matches.value = await geocodeAddress(address, { signal: controller.signal });
  } catch (error) {
    if (isAbortError(error)) return;

    console.error(error);
    matches.value = [];
  } finally {
    if (searchController === controller) {
      searchController = null;
      isLoading.value = false;
    }
  }
}

function add(location: GeocodedLocation | null) {
  if (!location) return;

  // the same place twice would be a duplicate line in the partner's booking
  if (location.place_id && locations.value.some((existing) => existing.place_id === location.place_id)) {
    return;
  }

  modelValue.value = [...locations.value, location];
  matches.value = [];
}

function remove(index: number) {
  const next = locations.value.filter((_, position) => position !== index);

  // null rather than [] once the last one goes, so PJAS reads the field as "not filled in" and a
  // required field is reported as missing instead of as an empty list
  modelValue.value = next.length ? next : null;
}

onUnmounted(() => searchController?.abort());
</script>

<style scoped lang="scss">
.form-label {
  display: block;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;

  &.required::after {
    content: '*';
    color: #ef4444;
    margin-left: 2px;
  }
}

.hint-text {
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  margin-bottom: 6px;
}

.location-list {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.location-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;

  & + & {
    margin-top: 6px;
  }
}

.location-text {
  min-width: 0;
}

.location-address {
  font-size: 14px;
  font-weight: 500;
  color: #101828;
  overflow-wrap: anywhere;
}

.location-parts {
  font-size: 12px;
  color: #667085;
}
</style>
